<template>
  <!-- Only on dev and QA, where every Work4All tenant has a backend of its own; not in
       an admin hub session, which belongs to the tenant it was opened on. -->
  <div v-if="canSwitchTenant && !authStore.isAdminHubSession" class="tenant-switcher">
    <Button
      severity="secondary"
      text
      class="max-w-40 md:max-w-72"
      aria-haspopup="true"
      :aria-expanded="menuOpen"
      :aria-label="t('nav.tenantSwitch.trigger', { name: currentName })"
      :loading="switching"
      @click="toggleMenu"
    >
      <i class="pi pi-database shrink-0" />
      <!-- The name needs the room a phone's header does not have -->
      <span class="hidden md:inline truncate font-semibold">{{ currentName }}</span>
      <span
        v-if="current?.read_only"
        class="shrink-0 rounded-xs bg-red-100 px-1.5 text-xs font-medium text-red-600"
      >
        {{ t('nav.tenantSwitch.readOnly') }}
      </span>
      <i class="pi pi-chevron-down shrink-0 text-xs" />
    </Button>

    <Menu
      ref="menu"
      :model="tenantItems"
      :popup="true"
      class="w-72"
      @show="menuOpen = true"
      @hide="menuOpen = false"
    >
      <template #start>
        <p class="px-3 pb-2 pt-3 text-xs font-semibold uppercase tracking-wide text-grey-400">
          {{ t('nav.tenantSwitch.title') }}
        </p>
      </template>

      <template #item="{ item, props }">
        <a
          class="flex items-center gap-3"
          :class="{ 'cursor-not-allowed opacity-50': item.disabled }"
          v-bind="props.action"
        >
          <i
            class="pi pi-check w-4 shrink-0 text-primary-500"
            :class="item.isActive ? 'opacity-100' : 'opacity-0'"
          />
          <span class="flex min-w-0 flex-col">
            <span class="truncate" :class="item.isActive ? 'font-semibold' : ''">
              {{ item.label }}
            </span>
            <span class="text-xs text-grey-500">
              {{ t('nav.tenantSwitch.tenantNo', { code: item.code }) }}
              <template v-if="item.readOnly"> · {{ t('nav.tenantSwitch.readOnly') }}</template>
              <template v-else-if="item.disabled"> · {{ t('nav.tenantSwitch.locked') }}</template>
            </span>
          </span>
        </a>
      </template>

      <template #end>
        <p class="mt-1 border-t border-grey-200 px-3 py-2 text-xs text-grey-500">
          {{ t('nav.tenantSwitch.loginNote') }}
        </p>
      </template>
    </Menu>

    <!-- Unsaved changes would be lost, as when switching the customer; the live tenant's
         real data are asked for as well -->
    <Dialog
      v-model:visible="showConfirm"
      modal
      :header="t(toLive ? 'nav.tenantSwitch.liveConfirmTitle' : 'nav.tenantSwitch.confirmTitle')"
      :style="{ width: '28rem' }"
      :draggable="false"
    >
      <p v-if="toLive" class="text-paragraph-text">
        {{ t('nav.tenantSwitch.liveConfirmBody', { name: tenantName(pending) }) }}
      </p>
      <p v-if="unsaved" class="text-paragraph-text" :class="{ 'mt-3': toLive }">
        {{
          t('nav.tenantSwitch.confirmBody', { name: pending === null ? '' : tenantName(pending) })
        }}
      </p>
      <template #footer>
        <Button
          :label="t('nav.tenantSwitch.cancel')"
          severity="secondary"
          outlined
          @click="cancelSwitch"
        />
        <Button
          :label="t(unsaved ? 'nav.tenantSwitch.confirm' : 'nav.tenantSwitch.liveConfirm')"
          :loading="switching"
          @click="pending !== null && performSwitch(pending)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import type { ITenant } from '@/type'
import {
  canSwitchTenant,
  canSwitchTo,
  currentTenantCode,
  LIVE_TENANT_CODE,
  loadTenants,
  selectTenant,
  tenantApis,
} from '@/utils/tenant'
import { hasUnsavedChanges } from '@/composables/useUnsavedChanges'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const authStore = useAuthStore()

const menu = ref<InstanceType<typeof Menu> | null>(null)
const menuOpen = ref(false)
const switching = ref(false)
const showConfirm = ref(false)
/** The tenant awaiting confirmation: the live one, or while there are unsaved changes. */
const pending = ref<number | null>(null)
const unsaved = ref(false)
const toLive = computed(() => pending.value === LIVE_TENANT_CODE)
/** What each tenant's backend says about itself, by tenant code. */
const tenants = ref<Record<number, ITenant>>({})

const currentCode = currentTenantCode()
/** The tenant the hub talks to now; unknown while its backend does not answer. */
const current = computed(() => (currentCode === null ? null : tenants.value[currentCode]))

/** A tenant's name, its number while its backend does not answer. */
function tenantName(code: number | null): string {
  if (code === null) return t('nav.tenantSwitch.title')
  return tenants.value[code]?.name ?? t('nav.tenantSwitch.tenantNo', { code })
}

const currentName = computed(() => tenantName(currentCode))

const tenantItems = computed<MenuItem[]>(() =>
  tenantApis.map((api) => ({
    label: tenantName(api.code),
    code: api.code,
    readOnly: tenants.value[api.code]?.read_only ?? false,
    isActive: api.code === currentCode,
    // The live tenant only while its backend says it is read-only
    disabled: !canSwitchTo(api.code, tenants.value[api.code]),
    command: () => selectTenantToSwitch(api.code),
  })),
)

function toggleMenu(event: Event) {
  menu.value?.toggle(event)
}

function selectTenantToSwitch(code: number) {
  if (code === currentCode || !canSwitchTo(code, tenants.value[code])) return
  pending.value = code
  unsaved.value = hasUnsavedChanges()
  if (unsaved.value || code === LIVE_TENANT_CODE) {
    showConfirm.value = true
    return
  }
  performSwitch(code)
}

function cancelSwitch() {
  showConfirm.value = false
  pending.value = null
}

/**
 * Each tenant has its own backend with its own users, so switching signs out here and
 * opens the login of the other tenant's backend.
 */
async function performSwitch(code: number) {
  if (code === currentCode || switching.value || !canSwitchTo(code, tenants.value[code])) return
  switching.value = true
  if (authStore.isAuthenticated) await authStore.logout()
  selectTenant(code)
  // A full reload, so every request goes to the other backend from now on
  window.location.assign(`${import.meta.env.BASE_URL}login`)
}

onMounted(async () => {
  if (canSwitchTenant) tenants.value = await loadTenants()
})
</script>
